import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * The session's CLIENT state, and nothing else.
 *
 * The user's email and tenants are server state: `GET /api/account` returns them
 * and RTK Query serves them (`useGetAccountQuery`). Holding them here too was a
 * second source of truth that could drift out of sync with the cache, so they
 * were removed: the only thing living in this slice is what the server does not
 * know.
 *
 * The two ids DO outlive the tab — `auth-storage.ts` mirrors them into
 * `localStorage` and `makeStore` hydrates from there — but the slice is not the
 * one doing it: it keeps its own contract of reconciling whatever it holds
 * against the lists the server returns, which is what makes a restored id safe.
 *
 * ## Where the DEFAULT comes from
 *
 * When what the slice holds is no good — nothing chosen, or an id that is no
 * longer in the list — something has to choose, and «the first of the list» is
 * a bad chooser: it lands whoever arrives with an empty browser on an empresa
 * that is not the one they work on. So both reconciliations take a
 * `preferredId`: what the server remembers this person was last on
 * (`GET /api/account`'s `lastTenantId` and each tenant's `lastCompanyId`, kept
 * by `PUT /api/account/selection`). It is only the DEFAULT — see the note on
 * each reducer for why it does not override a valid local choice.
 */
export interface AuthState {
  /** The active tenant (multi-tenant): the rest of the app filters by this id. */
  activeTenantId: string | null;
  /**
   * The active company of `AppShell`'s identity strip. It is client state for the
   * same reason as the tenant: the server returns the list of companies, but has no
   * opinion about which one this browser is looking at.
   *
   * It lives here and not in the layout because the active company's environment
   * (producción / certificación / sandbox) is what separates a test from a folio
   * consumed, and that decision has to survive any navigation — a `useState` in
   * the layout is lost as soon as the tree remounts — and, since it is persisted,
   * a reload too.
   */
  activeCompanyId: string | null;
  /**
   * An optional API key for programmatic mode. The user session lives in an
   * `HttpOnly` cookie and is NOT stored here (see docs/FRONTEND_PLAN.md §3.1).
   */
  apiKey: string | null;
  /**
   * The session was ended ON PURPOSE on this browser, which is not the same
   * thing as the two ids being `null`.
   *
   * Without the distinction, `null` reads as "nothing chosen yet" and the
   * reconciliation answers it the way it answers a first visit: by choosing a
   * default. That is right on a first visit and wrong immediately after
   * logging out, where the layout is still mounted, its cache has just been
   * emptied and it therefore asks for `GET /api/account` once more — see
   * `routes/AppLayout.tsx`, which reads this to stop asking.
   *
   * Nothing puts it back to `false`, and nothing needs to: signing in is a
   * real navigation (`/auth/login` leaves the SPA), so the next session starts
   * on a new document with a new store. It is not persisted either — see
   * `auth-storage.ts`, which stores the two ids and nothing else — so a reload
   * is a clean start as well.
   */
  sessionEnded: boolean;
}

/**
 * Everything unchosen. `makeStore` starts from this and lays the persisted
 * selection over it, so what is restored is exactly two ids: an api key is never
 * written to storage and never comes back from it.
 */
export const initialAuthState: AuthState = {
  activeTenantId: null,
  activeCompanyId: null,
  apiKey: null,
  sessionEnded: false,
};

/**
 * What to select when nothing valid is held: the remembered id if it is still in
 * the list, the list's first otherwise, `null` for an empty list. The membership
 * check is not redundant with the backend's — a list can change between the read
 * that produced the preference and this one.
 */
function pickDefault(ids: string[], preferredId: string | null): string | null {
  if (preferredId !== null && ids.includes(preferredId)) return preferredId;
  return ids[0] ?? null;
}

const authSlice = createSlice({
  name: 'auth',
  initialState: initialAuthState,
  reducers: {
    /**
     * Reconciles the active tenant with the tenants the account returns. It only
     * picks a default when the current one is no good (there is none, or it is no
     * longer in the list); if it is still valid it does not overwrite it, so as not
     * to undo the user's choice on every refetch of `GET /api/account`.
     *
     * `preferredId` is the organisation the SERVER remembers, and it wins over
     * the list's first — but not over a valid id already held. That order is
     * deliberate: what this browser holds is what this browser was last doing,
     * and letting the server's copy override it would mean a second device, or
     * even a second tab, could silently move the organisation under someone
     * mid-task. The remembered value is what a browser with NOTHING to hold
     * starts from, which is the case it exists for.
     *
     * It does nothing at all once the session has been ended: see
     * `sessionEnded`. A default chosen there would not be a first visit's
     * default, it would be the person who has just left.
     */
    availableTenantsResolved(
      state,
      action: PayloadAction<{ ids: string[]; preferredId?: string | null }>,
    ) {
      const { ids, preferredId = null } = action.payload;
      if (state.sessionEnded) return;
      if (
        state.activeTenantId === null ||
        !ids.includes(state.activeTenantId)
      ) {
        state.activeTenantId = pickDefault(ids, preferredId);
      }
    },
    activeTenantChanged(state, action: PayloadAction<string>) {
      state.activeTenantId = action.payload;
      // The companies hang off the tenant: the one that was active need not exist
      // in the new one. It is cleared and `availableCompaniesResolved` picks again
      // when the incoming tenant's list arrives — leaving it set is how the strip
      // would announce the environment of a company from another organisation.
      state.activeCompanyId = null;
    },
    /**
     * Reconciles the active company with the companies that exist. The same contract
     * as `availableTenantsResolved`: it only picks a default when the current one is
     * no good (there is none, or it is no longer in the list), so as not to undo the
     * user's choice on every refetch of `GET /api/companies`.
     *
     * `preferredId` is the empresa the server remembers FOR THE ACTIVE
     * ORGANISATION. Because the memory hangs off the membership and not off the
     * person, this is also what makes switching organisation and coming back
     * return to the empresa that was open there: `activeTenantChanged` clears
     * the company, and the incoming organisation's list arrives with its own
     * remembered id as the default.
     *
     * Ended sessions are refused here too, for the same reason.
     */
    availableCompaniesResolved(
      state,
      action: PayloadAction<{ ids: string[]; preferredId?: string | null }>,
    ) {
      const { ids, preferredId = null } = action.payload;
      if (state.sessionEnded) return;
      if (state.activeCompanyId === null || !ids.includes(state.activeCompanyId)) {
        state.activeCompanyId = pickDefault(ids, preferredId);
      }
    },
    activeCompanyChanged(state, action: PayloadAction<string>) {
      state.activeCompanyId = action.payload;
    },
    /**
     * Logging out. It also empties the persisted selection (the store's
     * listener writes on every change of these two ids), so the next person on
     * this browser starts from their own list.
     *
     * What the SERVER remembers is deliberately left alone: it belongs to the
     * account, not to the browser, and erasing it on the way out would defeat
     * the point — signing back in should land on the empresa you left.
     *
     * `sessionEnded` is what keeps the two reconciliations from choosing again
     * a moment later, which is not hypothetical: the layout doing the
     * reconciling is still mounted when this runs.
     */
    sessionCleared(state) {
      state.activeTenantId = null;
      state.activeCompanyId = null;
      state.apiKey = null;
      state.sessionEnded = true;
    },
  },
});

export const {
  availableTenantsResolved,
  activeTenantChanged,
  availableCompaniesResolved,
  activeCompanyChanged,
  sessionCleared,
} = authSlice.actions;
export const authReducer = authSlice.reducer;
